import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, Users } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_REP_EFFECTIVENESS_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, AttributionBanner, EmptyState, StatTile, num, pct, resolveDateRange,
    StatusPill,
    StatusFilterSelect,
} from "./adReportKit";

/**
 * Cell tint by how far this rep's close rate on this ad sits from the overall
 * average. The point of the grid is the *outliers* — one rep converting a
 * source everyone else fails on — so the colour encodes deviation, not level.
 */
const cellTone = (rate, overall) => {
    if (rate == null) return "bg-muted/30 text-muted-foreground";
    if (overall == null || overall === 0) return "bg-muted/50 text-foreground";
    const ratio = rate / overall;
    if (ratio >= 1.5) return "bg-emerald-100 dark:bg-emerald-900/35 text-emerald-800 dark:text-emerald-300 font-semibold";
    if (ratio >= 1.1) return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400";
    if (ratio >= 0.9) return "bg-muted/50 text-foreground";
    if (ratio >= 0.5) return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400";
    return "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
};

export default function RepAdEffectivenessReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_rep_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_rep_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_rep_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_rep_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_rep_status", "");
    const [level, setLevel] = usePersistedState("ad_rep_level", "campaign");
    const [minLeads, setMinLeads] = usePersistedState("ad_rep_minleads", "3");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams({ level, min_leads: minLeads });
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        fetch(`${AD_REP_EFFECTIVENESS_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, level, minLeads, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const reps = data?.data || [];
    const columns = data?.columns || [];
    const s = data?.summary;
    const wired = data?.attribution?.is_wired;

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Users className="h-6 w-6 text-primary" />
                            Rep × Ad Effectiveness
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Close rate by rep and by source — where one rep converts what another cannot.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <select value={level} onChange={(e) => setLevel(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="campaign">By campaign</option>
                        <option value="ad">By ad</option>
                    </select>
                    <select value={minLeads} onChange={(e) => setMinLeads(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="1">Any volume</option>
                        <option value="3">3+ leads</option>
                        <option value="10">10+ leads</option>
                        <option value="25">25+ leads</option>
                    </select>
                </AdFilterBar>

                <AttributionBanner attribution={data?.attribution} />

                {wired && s && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile label="Reps" value={num(s.reps)} />
                        <StatTile tone="leads" label="Attributed leads" value={num(s.leads)} />
                        <StatTile label="Won" value={num(s.won)} />
                        <StatTile tone="cost" label="Overall close rate" value={pct(s.win_rate, 1)} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && wired && reps.length === 0 && (
                    <EmptyState title="No attributed leads with an assigned rep"
                        hint="Leads must be both attributed to an ad and assigned to someone to appear here." />
                )}

                {!loading && wired && reps.length > 0 && (
                    <>
                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium sticky left-0 bg-muted/50 min-w-[130px]">Rep</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Leads</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Won</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Overall</th>
                                        {columns.map((c) => (
                                            <th key={c.id} className="px-2 py-2.5 text-center font-medium min-w-[92px]">
                                                <div className="truncate max-w-[110px] mx-auto" title={c.name}>{c.name}</div>
                                                <div className="flex justify-center mt-0.5">
                                                    <StatusPill status={c.effective_status} compact />
                                                </div>
                                                <div className="text-[9px] font-normal opacity-70 tabular-nums">
                                                    {c.leads} leads · {pct(c.win_rate, 0)}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {reps.map((r) => (
                                        <tr key={r.user_id} className="hover:bg-accent/20">
                                            <td className="px-3 py-2 font-medium text-foreground sticky left-0 bg-card">{r.name}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{num(r.leads)}</td>
                                            <td className="px-2 py-2 text-right text-foreground tabular-nums">{num(r.won)}</td>
                                            <td className="px-2 py-2 text-right font-semibold text-foreground tabular-nums">{pct(r.win_rate, 1)}</td>
                                            {columns.map((c) => {
                                                const cell = r.cells?.[c.id];
                                                return (
                                                    <td key={c.id} className="px-1.5 py-1.5">
                                                        <div className={cn("rounded-md px-1.5 py-1 text-center", cellTone(cell?.win_rate, s.win_rate))}
                                                            title={cell
                                                                ? `${cell.leads} leads · ${cell.demos} demos · ${cell.won} won`
                                                                : "no leads from this source"}>
                                                            <div className="tabular-nums text-[11px]">
                                                                {cell ? pct(cell.win_rate, 0) : "—"}
                                                            </div>
                                                            {cell && (
                                                                <div className="text-[9px] opacity-70 tabular-nums">
                                                                    {cell.won}/{cell.leads}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <p className="text-[11px] text-muted-foreground">
                            Cells are shaded by how far the rep's close rate sits from the {pct(s.win_rate, 1)} overall
                            average — green means well above, red well below. Columns below the volume threshold are
                            hidden, because a close rate over one or two leads is noise, not a finding.
                        </p>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
